import React, { useState } from 'react';

const DoctorCard = ({ doctorData, index, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(doctorData);
  const [scheduleJsonError, setScheduleJsonError] = useState('');

  const handleSave = () => {
    if (scheduleJsonError) {
      return;
    }
    onUpdate(index, editedData);
    setIsEditing(false);
    setScheduleJsonError('');
  };

  const handleCancel = () => {
    setEditedData(doctorData);
    setIsEditing(false);
    setScheduleJsonError('');
  };

  const handleFieldChange = (path, value) => {
    const keys = path.split('.');
    const newData = { ...editedData };
    let current = newData;

    for (let i = 0; i < keys.length - 1; i++) {
      current[keys[i]] = { ...current[keys[i]] };
      current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
    setEditedData(newData);
  };

  const handleScheduleJsonChange = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      handleFieldChange('schedule.schedule', parsed);
      setScheduleJsonError('');
    } catch (e) {
      setScheduleJsonError('Invalid JSON format');
    }
  };

  const doctor = editedData.doctor || {};
  const schedule = editedData.schedule || {};

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {doctor.primary_name || `Doctor ${index + 1}`}
        </h3>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={!!scheduleJsonError}
              className="text-green-600 hover:text-green-700 text-sm font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Save
            </button>
            <button
              onClick={handleCancel}
              className="text-gray-600 hover:text-gray-700 text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {/* Doctor Information */}
        <div>
          <h4 className="font-medium text-gray-700 mb-2">Doctor Information</h4>
          <div className="space-y-2 text-sm">
            <div>
              <span className="font-medium text-gray-600">Name:</span>{' '}
              {isEditing ? (
                <input
                  type="text"
                  value={doctor.primary_name || ''}
                  onChange={(e) => handleFieldChange('doctor.primary_name', e.target.value)}
                  className="ml-2 px-2 py-1 border border-gray-300 rounded"
                />
              ) : (
                <span className="text-gray-900">{doctor.primary_name || 'N/A'}</span>
              )}
            </div>
            <div>
              <span className="font-medium text-gray-600">Speciality:</span>{' '}
              {isEditing ? (
                <input
                  type="text"
                  value={doctor.speciality || ''}
                  onChange={(e) => handleFieldChange('doctor.speciality', e.target.value)}
                  className="ml-2 px-2 py-1 border border-gray-300 rounded"
                />
              ) : (
                <span className="text-gray-900">{doctor.speciality || 'N/A'}</span>
              )}
            </div>
            <div>
              <span className="font-medium text-gray-600">Gender:</span>{' '}
              {isEditing ? (
                <select
                  value={doctor.gender || ''}
                  onChange={(e) => handleFieldChange('doctor.gender', e.target.value || null)}
                  className="ml-2 px-2 py-1 border border-gray-300 rounded"
                >
                  <option value="">Not specified</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              ) : (
                <span className="text-gray-900">{doctor.gender || 'N/A'}</span>
              )}
            </div>
            <div>
              <span className="font-medium text-gray-600">Services:</span>
              {isEditing ? (
                <textarea
                  value={doctor.service_provided || ''}
                  onChange={(e) => handleFieldChange('doctor.service_provided', e.target.value)}
                  className="ml-2 w-full px-2 py-1 border border-gray-300 rounded"
                  rows={3}
                />
              ) : (
                <p className="text-gray-900 mt-1">{doctor.service_provided || 'N/A'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Schedule Information */}
        <div>
          <h4 className="font-medium text-gray-700 mb-2">Schedule</h4>
          <div className="text-sm space-y-1">
            <div>
              <span className="font-medium text-gray-600">From:</span>{' '}
              {isEditing ? (
                <input
                  type="date"
                  value={schedule.from_date || ''}
                  onChange={(e) => handleFieldChange('schedule.from_date', e.target.value || null)}
                  className="ml-2 px-2 py-1 border border-gray-300 rounded"
                />
              ) : (
                <span className="text-gray-900">{schedule.from_date || 'N/A'}</span>
              )}
            </div>
            <div>
              <span className="font-medium text-gray-600">To:</span>{' '}
              {isEditing ? (
                <input
                  type="date"
                  value={schedule.to_date || ''}
                  onChange={(e) => handleFieldChange('schedule.to_date', e.target.value || null)}
                  className="ml-2 px-2 py-1 border border-gray-300 rounded"
                />
              ) : (
                <span className="text-gray-900">{schedule.to_date || 'N/A'}</span>
              )}
            </div>
            <div>
              <span className="font-medium text-gray-600">Is Default Schedule:</span>{' '}
              {isEditing ? (
                <input
                  type="checkbox"
                  checked={schedule.is_default_schedule || false}
                  onChange={(e) => {
                    const newSchedule = {
                      ...schedule,
                      is_default_schedule: e.target.checked,
                      from_date: e.target.checked ? null : schedule.from_date,
                      to_date: e.target.checked ? null : schedule.to_date
                    };
                    handleFieldChange('schedule', newSchedule);
                  }}
                  className="ml-2"
                />
              ) : (
                <span className="text-gray-900">{schedule.is_default_schedule ? 'Yes' : 'No'}</span>
              )}
            </div>
            {schedule.schedule && (
              <div className="mt-2">
                <span className="font-medium text-gray-600">Working Hours:</span>
                {isEditing ? (
                  <div className="mt-1">
                    <textarea
                      value={JSON.stringify(schedule.schedule, null, 2)}
                      onChange={(e) => handleScheduleJsonChange(e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded font-mono text-xs"
                      rows={12}
                    />
                    {scheduleJsonError && (
                      <p className="text-red-600 text-xs mt-1">{scheduleJsonError}</p>
                    )}
                  </div>
                ) : (
                  <div className="mt-1 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                    <pre className="whitespace-pre-wrap">{JSON.stringify(schedule.schedule, null, 2)}</pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorCard;

